import React from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../utils/axiosConfig';
import Content from '../../components/Content';
import Loader from '../../components/Loader';
import s from './TriageBoard.module.css';

// Area key → sidebar glyph + the page "Investigate" jumps to.
const AREA_UI = {
	// `route` is where a single item's Investigate/Open jumps (it carries ?q=<ip>
	// for the filterable alerts list); `viewAll` overrides where the card's
	// "View all →" lands — Network Anomalies are SOC baseline/geo detections, so
	// the full view lives on SOC Analytics, not the raw Network Alerts list.
	network: { icon: '⇄', route: '/network-alerts', viewAll: '/soc-analytics', label: 'Network Anomalies' },
	ot: { icon: '⚙', route: '/ot-settings', label: 'OT / ICS' },
	attack_surface: { icon: '◎', route: '/external-baseline-comms', label: 'External Baseline' },
	threat_geo: { icon: '⌖', route: '/network-alerts', label: 'Threat Intel + Geo' },
	assets: { icon: '⊞', route: '/watcher-assets', label: 'New & Unknown Assets' },
	ids: { icon: '◊', route: '/ids-rules', label: 'IDS Signatures' },
	Coverage: { icon: '●', route: '/watcher-status', label: 'Coverage' },
};

// The threat_geo area bundles geo, ioc AND file alerts, which live on different
// pages: geo/ioc appear on the Network Alerts list (the area route), but file
// transfers have their own page. Route file precisely; the rest use the area.
const CATEGORY_ROUTE = {
	file: '/file-transfers',   // Suricata file transfers / IOC hash matches
	dns: '/morningwatch',      // domain-change events live on MorningWatch, not the area route
};
const sevVar = (sev) => `var(--sev-${sev || 'info'})`;
const tint = (sev, pct) => `color-mix(in srgb, var(--sev-${sev || 'info'}) ${pct}%, transparent)`;
const fmtAge = (m) => (m == null ? '' : m < 1 ? 'now' : m < 60 ? `${m}m` : `${Math.floor(m / 60)}h`);

function Spark({ data, sev }) {
	if (!data || !data.length) return null;
	const w = 78, h = 26, pad = 3;
	const max = Math.max(...data), min = Math.min(...data), rng = max - min || 1;
	const pts = data.map((v, i) => [pad + (i * (w - 2 * pad)) / (data.length - 1 || 1), h - pad - ((v - min) / rng) * (h - 2 * pad)]);
	const d = pts.map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ');
	const last = pts[pts.length - 1];
	const c = sevVar(sev);
	return (
		<svg className={s.spark} viewBox={`0 0 ${w} ${h}`} fill="none" aria-hidden="true">
			<path d={`${d} L${(w - pad).toFixed(1)} ${h - pad} L${pad} ${h - pad} Z`} fill={c} opacity=".12" />
			<path d={d} stroke={c} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
			<circle cx={last[0].toFixed(1)} cy={last[1].toFixed(1)} r="2.2" fill={c} />
		</svg>
	);
}

// "From Zone X → Zone Y" for an alert. The backend resolves each endpoint IP
// against the client's net_zones (External for routable addresses, Unzoned for
// an internal IP in no defined zone) and hands us the zone name + its colour.
// Only rendered when at least one end resolved, so non-network items stay clean.
function ZoneChip({ name, color }) {
	if (!name) return null;
	return (
		<span className={s.zoneChip} title={`Zone: ${name}`}>
			<i className={s.zoneDot} style={{ background: color || 'var(--text-secondary)' }} />
			{name}
		</span>
	);
}

function ZoneFlow({ item }) {
	if (!item || (!item.src_zone && !item.dst_zone)) return null;
	return (
		<span className={s.zoneFlow}>
			<ZoneChip name={item.src_zone} color={item.src_zone_color} />
			{item.src_zone && item.dst_zone && <span className={s.zoneArrow}>→</span>}
			<ZoneChip name={item.dst_zone} color={item.dst_zone_color} />
		</span>
	);
}

const TriageBoardScreen = () => {
	const navigate = useNavigate();
	const [data, setData] = React.useState(null);
	const [error, setError] = React.useState('');
	const [loading, setLoading] = React.useState(true);
	// Multi-select acknowledge: a set of selected alert ref_ids across every list.
	const [selected, setSelected] = React.useState(() => new Set());
	const [ackBusy, setAckBusy] = React.useState(false);

	const load = React.useCallback(async () => {
		try {
			const { data } = await axios.get('/api/dashboard/triage');
			if (data && data.status) { setData(data); setError(''); }
			else setError('Could not load the dashboard.');
		} catch (e) {
			setError((e && e.response && e.response.data && e.response.data.detail) || 'Could not load the dashboard.');
		} finally {
			setLoading(false);
		}
	}, []);

	React.useEffect(() => {
		load();
		const id = setInterval(load, 30000);
		return () => clearInterval(id);
	}, [load]);

	// Every alert id behind an item — the whole correlated cluster if the backend
	// collapsed duplicates (ref_ids), else just the representative (ref_id).
	const idsOf = (item) => (Array.isArray(item.ref_ids) && item.ref_ids.length
		? item.ref_ids : (item.ref_id != null ? [item.ref_id] : []));

	// The pending Ack / Ack & Allow, held open while the operator (optionally)
	// types a note. { ids: string[], allow: bool, label: string }.
	const [ackModal, setAckModal] = React.useState(null);
	const [ackNote, setAckNote] = React.useState('');

	const doAck = React.useCallback(async (ids, allow, note) => {
		if (!ids || !ids.length) return;
		setAckBusy(true);
		try {
			const fd = new FormData();
			fd.append('alertIds', ids.join(','));
			if (allow) fd.append('allow', '1');
			if (note && note.trim()) fd.append('note', note.trim());
			await axios.post('/api/acknowledgeAlert', fd, { headers: { 'Content-Type': 'application/json' } });
			setSelected(new Set());
			await load();
		} catch { /* leave it; the next refresh reconciles */ }
		finally { setAckBusy(false); }
	}, [load]);

	const isAckable = (item) => item && item.kind === 'alert' && item.ref_id != null;
	// Categories where "Ack & Allow" can safely whitelist (SOC detections, denied
	// country, OT commands). IDS/IOC/file are threat signals — ack-only.
	const canAllow = (item) => isAckable(item) && ['geo', 'soc', 'ot'].includes(item.category);

	// Open the note modal for a single item's whole cluster.
	const acknowledge = (item, allow = false) => {
		if (!isAckable(item)) return;
		setAckNote('');
		setAckModal({ ids: idsOf(item).map(String), allow, label: item.title || 'this alert' });
	};

	const toggleSelect = (item) => {
		if (!isAckable(item)) return;
		setSelected((prev) => {
			const next = new Set(prev);
			const key = String(item.ref_id);
			if (next.has(key)) next.delete(key); else next.add(key);
			return next;
		});
	};

	// Every acknowledgeable alert currently on the board (Act Now + all areas).
	const ackableRefIds = React.useMemo(() => {
		if (!data) return [];
		const ids = new Set();
		(data.act_now || []).forEach((it) => { if (isAckable(it)) ids.add(String(it.ref_id)); });
		(data.areas || []).forEach((a) => (a.items || []).forEach((it) => { if (isAckable(it)) ids.add(String(it.ref_id)); }));
		return Array.from(ids);
	}, [data]);

	const allShownSelected = ackableRefIds.length > 0 && ackableRefIds.every((id) => selected.has(id));

	const toggleSelectAllShown = () => {
		setSelected(() => (allShownSelected ? new Set() : new Set(ackableRefIds)));
	};

	const ackSelected = (allow = false) => {
		if (selected.size === 0) return;
		// Expand each selected item to its full correlated cluster, so acking a
		// collapsed "×47" row clears all 47, not just the representative.
		const ids = new Set();
		const collect = (it) => { if (isAckable(it) && selected.has(String(it.ref_id))) idsOf(it).forEach((x) => ids.add(String(x))); };
		(data?.act_now || []).forEach(collect);
		(data?.areas || []).forEach((a) => (a.items || []).forEach(collect));
		if (ids.size === 0) return;
		setAckNote('');
		setAckModal({ ids: Array.from(ids), allow, label: `${selected.size} selected item(s)` });
	};

	const investigate = (item) => {
		const byCat = item.category && CATEGORY_ROUTE[item.category];
		const byArea = AREA_UI[item.area] && AREA_UI[item.area].route;
		const route = byCat || byArea || '/network-alerts';
		// Carry what identifies THIS event so the target page can filter/scroll to
		// it instead of just landing on the list. For an IDS signature that's the
		// rule's SID (opens the exact rule that fired); otherwise the source IP,
		// else the host/domain. Pages that don't read ?q= simply ignore it.
		const isIds = item.category === 'ids';
		const term = (isIds && item.rule_sid ? String(item.rule_sid) : '')
			|| item.src_ip || (item.host && !item.host.includes('→') ? item.host : '') || '';
		let url = term ? `${route}?q=${encodeURIComponent(term)}` : route;
		// For an IDS rule, also carry the owning company so the IDS Rules page can
		// select the right source (a provider sees many companies' rulesets).
		if (isIds && item.company) {
			url += `${url.includes('?') ? '&' : '?'}company=${encodeURIComponent(item.company)}`;
		}
		navigate(url);
	};
	const block = () => navigate('/blocking');

	const provider = data && data.is_provider;

	const Actions = ({ item, queue }) => (
		<span className={s.acts}>
			{queue && <button type="button" className={s.invest} onClick={() => investigate(item)}>Investigate</button>}
			{item.can_block && item.src_ip && <button type="button" onClick={block}>Block</button>}
			{item.kind === 'alert' && (
				<button type="button" onClick={() => acknowledge(item)}
					title={item.count > 1 ? `Acknowledge all ${item.count} occurrences` : 'Acknowledge'}>
					{item.count > 1 ? 'Ack all' : 'Ack'}
				</button>
			)}
			{canAllow(item) && (
				<button type="button" className={s.allowBtn} onClick={() => acknowledge(item, true)}
					title="Acknowledge and allowlist this so it won't alert again">
					Ack &amp; Allow
				</button>
			)}
			{!queue && <button type="button" onClick={() => investigate(item)}>Open</button>}
		</span>
	);

	const Row = ({ item, queue }) => (
		<li className={`${queue ? s.qItem : s.qItem} ${item.kind === 'coverage' ? s.coverageRow : ''} ${isAckable(item) && selected.has(String(item.ref_id)) ? s.selectedRow : ''}`}>
			<span className={s.sv} style={{ background: sevVar(item.severity) }} />
			{queue && <span className={s.areaTag}>{(AREA_UI[item.area] && AREA_UI[item.area].label) || item.area}</span>}
			<div>
				<div className={s.t}>
					{item.minutes != null && item.minutes <= 15 && item.kind !== 'coverage' && <span className={s.newDot} title="new" />}
					{item.title}
					{item.count > 1 && (
						<span className={s.countBadge} title={`${item.count} correlated occurrences`}>×{item.count}</span>
					)}
				</div>
				<div className={s.meta}>
					<span className={s.pill} style={{ color: sevVar(item.severity), background: tint(item.severity, 14) }}>{item.severity}</span>
					{item.host && <span className={s.host}>{item.host}</span>}
					<ZoneFlow item={item} />
					{provider && item.company && <span className={s.co}>{item.company}</span>}
				</div>
			</div>
			<div className={s.right}>
				{isAckable(item) && (
					<input
						type="checkbox"
						className={s.selBox}
						checked={selected.has(String(item.ref_id))}
						onChange={() => toggleSelect(item)}
						title="Select to bulk-acknowledge"
					/>
				)}
				<span className={s.when}>{fmtAge(item.minutes)}</span>
				<Actions item={item} queue={queue} />
			</div>
		</li>
	);

	if (loading && !data) return <Content currentMenu="home" headerTitle="Dashboard"><Loader /></Content>;
	if (error && !data) return <Content currentMenu="home" headerTitle="Dashboard"><div className={s.error}>{error}</div></Content>;
	if (!data) return <Content currentMenu="home" headerTitle="Dashboard"><div className={s.error}>No data.</div></Content>;

	const { summary, coverage, fleet, act_now: actNow, areas } = data;
	const watcherHealth = data.watcher_health || [];
	const healthSummary = data.health_summary || { up: 0, down: 0, acked: 0 };
	// Colour a host-health reading by threshold — mirrors the SOC alerter and the
	// Watcher Status page (disk 75/85, mem 85/95, cpu 150/300 % of cores).
	const metricColor = (kind, v) => {
		if (typeof v !== 'number') return 'var(--text-secondary)';
		const t = kind === 'disk' ? [75, 85] : kind === 'mem' ? [85, 95] : [150, 300];
		return v >= t[1] ? sevVar('critical') : v >= t[0] ? sevVar('medium') : sevVar('ok');
	};
	const pctCell = (v) => (typeof v === 'number' ? `${Math.round(v)}%` : '—');

	return (
		<Content currentMenu="home" headerTitle="Dashboard">
			<div className={s.board}>
				{/* triage bar */}
				<section className={s.triage} aria-label="Triage summary">
					<div className={s.tcard}>
						<span className={s.stripe} style={{ background: sevVar('critical') }} />
						<div className={s.lab}>Needs attention</div>
						<div className={s.big}>{summary.needs_attention}</div>
						<div className={s.sub}>
							{provider ? `across ${coverage.total} appliances` : 'this environment'}
							{summary.oldest_minutes != null ? ` · oldest ${fmtAge(summary.oldest_minutes)}` : ''}
						</div>
						{provider && fleet && fleet.length > 0 && (
							<div className={s.fleet}>
								{fleet.map((f) => <span className={s.chip} key={f.company}>{f.company} <b>{f.criticals}</b></span>)}
							</div>
						)}
					</div>
					{[['critical', 'Critical'], ['high', 'High'], ['medium', 'Medium']].map(([k, lbl]) => (
						<div className={s.tcard} key={k}>
							<span className={s.stripe} style={{ background: sevVar(k) }} />
							<div className={s.lab}>{lbl}</div>
							<div className={s.big} style={{ color: sevVar(k) }}>{summary[k]}</div>
							<div className={s.sub}>open</div>
						</div>
					))}
				</section>

				{/* coverage */}
				<div className={s.coverage}>
					<span className={s.cvLab}>{provider ? 'Appliance coverage' : 'Coverage'}</span>
					<span className={s.bar}>
						{coverage.reporting > 0 && <i style={{ flex: coverage.reporting, background: sevVar('ok') }} />}
						{coverage.grace > 0 && <i style={{ flex: coverage.grace, background: sevVar('medium') }} />}
						{coverage.silent > 0 && <i style={{ flex: coverage.silent, background: sevVar('critical') }} />}
					</span>
					<span className={s.cvStat}>
						<b>{coverage.reporting}</b> reporting{coverage.grace ? <> · <b>{coverage.grace}</b> grace</> : null}{coverage.silent ? <> · <b>{coverage.silent}</b> silent</> : null}
					</span>
					{coverage.silent > 0
						? <span className={s.cvAlert}>{coverage.silent_list[0] ? `${coverage.silent_list[0].name} silent ${fmtAge(coverage.silent_list[0].minutes)}` : `${coverage.silent} silent`}</span>
						: <span className={s.cvAlert} style={{ color: sevVar('ok') }}>All healthy</span>}
				</div>

				{/* watcher health: up/down + drive/mem/cpu from the box's self-report */}
				{watcherHealth.length > 0 && (
					<section className={s.card} aria-label="Watcher health" style={{ marginTop: 12 }}>
						<div className={s.head}>
							<span className={s.ico} style={{ color: sevVar('ok'), background: tint('ok', 12) }}>◉</span>
							<h2>Watcher Health<span className={s.cnt}>{healthSummary.up}/{watcherHealth.length}</span></h2>
							<span className={s.spacer} />
							<span style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
								<b style={{ color: sevVar('ok') }}>{healthSummary.up}</b> up
								{healthSummary.down ? <> · <b style={{ color: sevVar('critical') }}>{healthSummary.down}</b> down</> : null}
								{healthSummary.acked ? <> · {healthSummary.acked} ack</> : null}
							</span>
						</div>
						<div style={{ maxHeight: 260, overflowY: 'auto' }}>
							<table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
								<thead>
									<tr style={{ textAlign: 'left', color: 'var(--text-secondary)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em' }}>
										<th style={{ padding: '4px 8px', fontWeight: 600 }}>Watcher</th>
										<th style={{ padding: '4px 8px', fontWeight: 600 }}>Status</th>
										<th style={{ padding: '4px 8px', fontWeight: 600, textAlign: 'right' }}>Drive</th>
										<th style={{ padding: '4px 8px', fontWeight: 600, textAlign: 'right' }}>Mem</th>
										<th style={{ padding: '4px 8px', fontWeight: 600, textAlign: 'right' }}>CPU</th>
									</tr>
								</thead>
								<tbody>
									{watcherHealth.map((h, i) => {
										const stateColor = h.online ? sevVar('ok') : (h.acked ? 'var(--text-secondary)' : sevVar('critical'));
										const stateText = h.online ? 'Up' : (h.acked ? 'Ack' : `Down ${fmtAge(h.minutes)}`);
										const driveTitle = typeof h.disk_free_gb === 'number' ? `${h.disk_free_gb} GB free` : undefined;
										return (
											<tr key={(h.name || '') + i} style={{ borderTop: '1px solid var(--border, rgba(127,127,127,.18))' }}>
												<td style={{ padding: '5px 8px', whiteSpace: 'nowrap' }}>
													{h.name}
													{provider && h.company && <span style={{ color: 'var(--text-secondary)', marginLeft: 6, fontSize: 11 }}>{h.company}</span>}
												</td>
												<td style={{ padding: '5px 8px', whiteSpace: 'nowrap' }}>
													<i style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: stateColor, marginRight: 6, verticalAlign: 'middle' }} />
													<span style={{ color: stateColor }}>{stateText}</span>
												</td>
												<td title={driveTitle} style={{ padding: '5px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: metricColor('disk', h.disk), fontWeight: (typeof h.disk === 'number' && h.disk >= 75) ? 700 : 400 }}>{pctCell(h.disk)}</td>
												<td style={{ padding: '5px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: metricColor('mem', h.mem), fontWeight: (typeof h.mem === 'number' && h.mem >= 85) ? 700 : 400 }}>{pctCell(h.mem)}</td>
												<td style={{ padding: '5px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: metricColor('cpu', h.cpu), fontWeight: (typeof h.cpu === 'number' && h.cpu >= 150) ? 700 : 400 }}>{pctCell(h.cpu)}</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
						<div style={{ padding: '6px 10px', fontSize: 11, color: 'var(--text-secondary)' }}>
							Drive / Mem / CPU from each box's self-report · “—” until a box reports (needs 8.0.137+)
						</div>
					</section>
				)}

				{/* act now */}
				<section className={s.actnow} aria-label="Act now">
					<div className={s.qhead}>
						<h2>Act Now<span className={s.cnt}>{actNow.length}</span></h2>
						<span className={s.live}>LIVE</span>
						<span className={s.spacer} />
						{ackableRefIds.length > 0 && (
							<span className={s.bulkBar}>
								<button type="button" className={s.bulkBtn} onClick={toggleSelectAllShown}>
									{allShownSelected ? 'Clear all' : `Select all (${ackableRefIds.length})`}
								</button>
								<button
									type="button"
									className={`${s.bulkBtn} ${s.bulkAck}`}
									onClick={() => ackSelected(false)}
									disabled={selected.size === 0 || ackBusy}
								>
									{ackBusy ? 'Acknowledging…' : `Ack selected (${selected.size})`}
								</button>
							</span>
						)}
						<span className={s.refresh}>auto-refresh 30s</span>
					</div>
					{actNow.length === 0
						? <div className={s.empty}>Nothing critical or high open right now.</div>
						: <ul className={s.qList}>{actNow.map((it, i) => <Row key={`an-${it.kind}-${it.ref_id || i}`} item={it} queue />)}</ul>}
					<div className={s.qFoot}>
						<span>Critical + High across every area · a silent sensor ranks here too</span>
					</div>
				</section>

				{/* area grid */}
				<main className={s.grid}>
					{areas.map((a) => {
						const ui = AREA_UI[a.key] || { icon: '•' };
						return (
							<section className={s.card} key={a.key}>
								<div className={s.head}>
									<span className={s.ico} style={{ color: sevVar(a.tone), background: tint(a.tone, 12) }}>{ui.icon}</span>
									<h2>{a.label}<span className={s.cnt}>{a.count}</span></h2>
									{a.key === 'ot' && <span className={s.lic}>OT&nbsp;licensed</span>}
									{a.delta > 0 && <span className={s.delta} style={{ color: sevVar('critical'), background: tint('critical', 12) }}>+{a.delta}</span>}
									<Spark data={a.spark} sev={a.tone} />
								</div>
								{a.items.length === 0
									? <div className={s.empty}>No open items.</div>
									: <ul className={s.items}>{a.items.map((it, i) => <Row key={`${a.key}-${it.ref_id || i}`} item={it} />)}</ul>}
								<div className={s.foot}>
									<span>{a.count} open{a.delta > 0 ? ` · ${a.delta} new this hour` : ''}</span>
									<a onClick={() => navigate(ui.viewAll || ui.route || '/')}>View all →</a>
								</div>
							</section>
						);
					})}
				</main>
			</div>

			{ackModal && (
				<div className={s.ackOverlay} onClick={() => setAckModal(null)}>
					<div className={s.ackModal} onClick={(e) => e.stopPropagation()}>
						<h3 className={s.ackModalTitle}>{ackModal.allow ? 'Acknowledge & Allow' : 'Acknowledge'}</h3>
						<p className={s.ackModalSub}>
							{ackModal.label}{ackModal.ids.length > 1 ? `  ·  ${ackModal.ids.length} alerts` : ''}
							{ackModal.allow ? '  ·  will be allowlisted (won’t re-alert)' : ''}
						</p>
						<textarea
							autoFocus
							className={s.ackTextarea}
							placeholder="Add a note (optional) - e.g. why this is expected"
							value={ackNote}
							onChange={(e) => setAckNote(e.target.value)}
						/>
						<div className={s.ackModalBtns}>
							<button type="button" className={s.bulkBtn} onClick={() => setAckModal(null)}>Cancel</button>
							<button
								type="button"
								className={`${s.bulkBtn} ${ackModal.allow ? s.allowBtn : s.bulkAck}`}
								disabled={ackBusy}
								onClick={() => { const m = ackModal; setAckModal(null); doAck(m.ids, m.allow, ackNote); }}
							>
								{ackModal.allow ? 'Acknowledge & Allow' : 'Acknowledge'}
							</button>
						</div>
					</div>
				</div>
			)}
		</Content>
	);
};

export default TriageBoardScreen;
